import { useState, useEffect } from 'react';
import { Pane, TextInputField, majorScale, Button, toaster, Select, FormField, Text } from 'evergreen-ui';
import { updateGuest, fetchGuestAttachments, uploadPei, uploadGuestAttachments, deleteGuestAttachment, deletePei } from '../services/guestsServices';
import { ConfirmIcon, CrossIcon } from 'evergreen-ui';
import FileUploaderSingleUploadDemo from '../commons/FileUploaderSingleUploadDemo';
import FileUploaderMultipleUploadDemo from '../commons/FileUploaderMultipleUploadDemo';

const EditGuestForm = ({ editedGuest, handleCancelEdit, fetchGuests }) => {
    const [localGuest, setLocalGuest] = useState({ ...editedGuest });
    const [guestAttachments, setGuestAttachments] = useState([]);
    const [peiVisible, setPeiVisible] = useState(Boolean(editedGuest?.revisione_pei_url));
    const [peiSelected, setPeiSelected] = useState(null); 
    const [attachmentsSelected, setAttachmentsSelected] = useState([]);
    const [attachmentsMarkedForDeletion, setAttachmentsMarkedForDeletion] = useState([]); 
    const [peiMarkedForDeletion, setPeiMarkedForDeletion] = useState(false);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        setLocalGuest((prev) => ({
            ...prev,
            id: editedGuest.id || prev.id,
        }));
    }, [editedGuest]);

    useEffect(() => {
        const loadAttachments = async () => {
            if (!editedGuest || !editedGuest.id) return;
            try {
                const atts = await fetchGuestAttachments(editedGuest.id);
                setGuestAttachments(atts || []);
                setPeiVisible(Boolean(editedGuest.revisione_pei_url));
            } catch (err) {
                console.error('Errore fetch attachments nel form:', err);
            }
        };
        loadAttachments();
    }, [editedGuest]);

    const handleInputChange = (field, value) => {
        setLocalGuest((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };

    const removePeiVisually = () => {
        setPeiMarkedForDeletion(true);
        setPeiVisible(false);
    };

    const handleAttachmentsChangeLocal = (files) => {
        setAttachmentsSelected(files || []);
    };

    const removeAttachmentVisually = (attachmentId) => {
        setAttachmentsMarkedForDeletion((prev) => (prev.includes(attachmentId) ? prev : [...prev, attachmentId]));
        setGuestAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    };

    const handleCancel = async () => {
        let atts = [];
        try {
            if (editedGuest && editedGuest.id) {
                atts = await fetchGuestAttachments(editedGuest.id);
            }
        } catch (err) {
            console.error('Errore reload attachments on cancel:', err);
        }
        setGuestAttachments(atts || []);
        setLocalGuest({ ...editedGuest });
        setAttachmentsMarkedForDeletion([]);
        setPeiMarkedForDeletion(false);
        setPeiSelected(null);
        setAttachmentsSelected([]);
        setPeiVisible(Boolean(editedGuest?.revisione_pei_url));
        handleCancelEdit();
    };

    const handleConfirmEdit = async () => {
        setProcessing(true);
        try {
            for (const attId of attachmentsMarkedForDeletion) {
                try {
                    await deleteGuestAttachment(attId);
                } catch (err) {
                    console.error('Errore rimozione allegato durante conferma:', err);
                }
            }

            if (peiMarkedForDeletion) {
                try {
                    await deletePei(editedGuest.id);
                } catch (err) {
                    console.error('Errore rimozione PEI durante conferma:', err);
                }
            }

            let newPeiUrl = localGuest.revisione_pei_url || null;
            if (peiSelected) {
                try {
                    const res = await uploadPei(editedGuest.id, peiSelected);
                    if (res && res.pei_url) {
                        newPeiUrl = res.pei_url;
                    }
                } catch (err) {
                    console.error('Errore upload PEI durante conferma:', err);
                }
            } else if (peiMarkedForDeletion) {
                newPeiUrl = null;
            }

            const payload = {
                nome: localGuest.nome,
                cognome: localGuest.cognome,
                data_nascita: localGuest.data_nascita,
                residenza: localGuest.residenza,
                domicilio: localGuest.domicilio,
                sesso: localGuest.sesso,
                luogo_nascita: localGuest.luogo_nascita,
                motivazione: localGuest.motivazione,
                revisione_pei_url: newPeiUrl,
            };

            const resultUpdate = await updateGuest(editedGuest.id, payload);
        
            if (!resultUpdate) {
                toaster.danger("Errore durante l'aggiornamento dell'ospite.");
                setProcessing(false);
                return;
            }

            if (attachmentsSelected && attachmentsSelected.length > 0) {
                try {
                    await uploadGuestAttachments(editedGuest.id, attachmentsSelected);
                } catch (err) {
                    console.error('Errore upload allegati durante conferma:', err);
                }
            }

            let atts = [];
            try {
                atts = await fetchGuestAttachments(editedGuest.id);
                setGuestAttachments(atts || []);
            } catch (err) {
                console.error('Errore refresh attachments dopo conferma:', err);
            }

            await fetchGuests();

            toaster.success('Modifiche salvate con successo.');

            setPeiSelected(null);
            setAttachmentsSelected([]);
            setAttachmentsMarkedForDeletion([]);
            setPeiMarkedForDeletion(false);

            handleCancelEdit();
        } catch (error) {
            console.error("Errore nella modifica dell'ospite:", error);
            toaster.danger("Errore nella modifica dell'ospite.");
        }
        setProcessing(false);
    };

    return (
        <Pane>
            <TextInputField
                label="Nome"
                value={localGuest.nome || ''}
                onChange={(e) => handleInputChange('nome', e.target.value)}
                marginBottom={16}
            />
            <TextInputField
                label="Cognome"
                value={localGuest.cognome || ''}
                onChange={(e) => handleInputChange('cognome', e.target.value)}
                marginBottom={16}
            />
            <FormField label="Sesso" marginBottom={16}>
                <Select id="sex-select" value={localGuest.sesso || 'M'} onChange={(e) => handleInputChange('sesso', e.target.value)}>
                    <option value="M">Maschio</option>
                    <option value="F">Femmina</option>
                </Select>
            </FormField>
            <TextInputField
                label="Data di nascita"
                type="date"
                value={localGuest.data_nascita}
                onChange={(e) => handleInputChange('data_nascita', e.target.value)}
                marginBottom={16}
            />
            <TextInputField
                label="Luogo di nascita"
                value={localGuest.luogo_nascita}
                onChange={(e) => handleInputChange('luogo_nascita', e.target.value)}
                marginBottom={16}
            />
            <TextInputField
                label="Residenza"
                value={localGuest.residenza}
                onChange={(e) => handleInputChange('residenza', e.target.value)}
                marginBottom={16}
            />
            <TextInputField
                label="Domicilio"
                value={localGuest.domicilio}
                onChange={(e) => handleInputChange('domicilio', e.target.value)}
                marginBottom={16}
            />
            <FormField label="Motivazione" marginBottom={16}>
                <Select
                    id="motivation-select"
                    value={localGuest.motivazione || 'minori stranieri non accompagnati'}
                    onChange={(e) => handleInputChange('motivazione', e.target.value)}
                >
                    <option value="minori stranieri non accompagnati">Minori stranieri non accompagnati</option>
                    <option value="penale">Penale</option>
                    <option value="sociale">Sociale</option>
                    <option value="emergenza">Emergenza</option>
                </Select>
            </FormField>

            <Pane marginTop={majorScale(4)}>
                <Text fontWeight={600}>PEI</Text>
                <Pane marginTop={8}>
                    {peiVisible && editedGuest.revisione_pei_url ? (
                        <Pane display="flex" alignItems="center" gap={12}>
                            <Text cursor="pointer" onClick={() => window.open(editedGuest.revisione_pei_url, '_blank')}>
                                {editedGuest.revisione_pei_url.split('/').pop()}
                            </Text>
                            <Button appearance="primary" intent="danger" onClick={removePeiVisually}>
                                Rimuovi
                            </Button>
                        </Pane>
                    ) : (
                        <Text>Nessun PEI caricato.</Text>
                    )}

                    <Pane marginTop={8} display="flex" alignItems="center" gap={8}>
                        <FileUploaderSingleUploadDemo onFileChange={(file) => setPeiSelected(file)} />
                    </Pane>
                </Pane>

                <Text fontWeight={600} marginTop={16}>Allegati</Text>
                <Pane marginTop={8}>
                    <Pane display="flex" flexDirection="column" gap={8}>
                        {(guestAttachments && guestAttachments.length > 0) ? (
                            guestAttachments.map((att) => (
                                <Pane key={att.id} display="flex" alignItems="center" gap={8}>
                                    <Text cursor="pointer" onClick={() => window.open(att.file_url, '_blank')}>
                                        {att.file_name}
                                    </Text>
                                    <Button appearance="primary" intent="danger" onClick={() => removeAttachmentVisually(att.id)}>
                                        Rimuovi
                                    </Button>
                                </Pane>
                            ))
                        ) : (
                            <Text>Nessun allegato.</Text>
                        )}
                    </Pane>

                    <Pane marginTop={8} display="flex" flexDirection="column" gap={8}>
                        <FileUploaderMultipleUploadDemo onFilesChange={handleAttachmentsChangeLocal} />
                    </Pane>
                </Pane>
            </Pane>

            <Pane display="flex" justifyContent="space-between" marginTop={majorScale(4)}>
                <Button appearance="primary" intent="success" onClick={handleConfirmEdit} disabled={processing}>
                    {processing ? 'Salvo...' : 'Conferma modifica'} <ConfirmIcon marginLeft={8} />
                </Button>
                <Button intent="danger" onClick={handleCancel} disabled={processing}>
                    Annulla <CrossIcon marginLeft={8} />
                </Button>
            </Pane>
        </Pane>
    );
};

export default EditGuestForm;